import type { CSSProperties } from 'react';

// ─── Fixed wave-pattern background ─────────────────────────────────────────────
//
// The site-wide wave motif: white parallel waves on the secondary color, under
// a muting secondary overlay. Rendered as an SVG data-URI so it can tile as a
// CSS background with `background-attachment: fixed` (the texture stays put
// while the page scrolls over it). Not a registered component (no
// contentSchema), so the schema linter skips it. Purely decorative — every
// layer is aria-hidden so screen readers skip it.
//
// The tile repeats SEAMLESSLY in both directions:
//   • Horizontal — the `q70 … t140 0` path returns to its baseline (and slope)
//     at x = WAVELENGTH, so the next tile continues without a step.
//   • Vertical — the tile height is an exact multiple of the line spacing, and
//     enough extra rows are drawn above/below (derived from the crest depth
//     AMP/2) that every crest clipped at a tile edge is completed by the
//     adjacent tile — even when the crest depth exceeds the spacing (mobile).
const WAVELENGTH = 280; // horizontal period (two humps per tile)
const LINE_SPACING = 15; // vertical distance between wave baselines
const AMP = 25; // quadratic control offset; peak deviation = AMP/2 = 12.5
const LINE_COUNT = 8;
const TILE_W = WAVELENGTH;
const LINE_COLOR = '#ffffff';

// The returned `tileH` is the CSS backgroundSize height, NOT the graphic
// height: shrinking the spacing only densifies the texture.
function buildWaveDataUri(spacing: number): { uri: string; tileH: number } {
  const tileH = spacing * LINE_COUNT;
  const extra = Math.ceil(AMP / 2 / spacing);
  const lines: string[] = [];
  for (let i = -extra; i <= LINE_COUNT + extra; i++) {
    const y = i * spacing;
    lines.push(
      `<path d='M0 ${y} q70 ${-AMP} 140 0 t140 0' fill='none' stroke='${LINE_COLOR}' stroke-width='1.5'/>`,
    );
  }
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${TILE_W}' height='${tileH}'>${lines.join('')}</svg>`;
  return { uri: `url("data:image/svg+xml,${encodeURIComponent(svg)}")`, tileH };
}

const DESKTOP = buildWaveDataUri(LINE_SPACING);
const MOBILE = buildWaveDataUri(LINE_SPACING / 2);

const layer = (bg: { uri: string; tileH: number }): CSSProperties => ({
  backgroundColor: 'var(--vibe-color-secondary)',
  backgroundImage: bg.uri,
  backgroundRepeat: 'repeat',
  backgroundSize: `${TILE_W}px ${bg.tileH}px`,
});

/**
 * Absolutely-positioned wave texture + muting overlay. Drop it inside any
 * `relative overflow-hidden` container; it fills the container (inset-0) and
 * sits at z-0 / z-[1], so content should use z-10 or higher.
 *
 * Desktop (≥ lg): standard spacing, fixed to the viewport.
 * Mobile + tablet (< lg): half spacing (denser waves), scrolls normally.
 */
export function FixedWaveTexture() {
  return (
    <>
      <div aria-hidden="true" className="absolute inset-0 z-0 bg-scroll lg:hidden" style={layer(MOBILE)} />
      <div aria-hidden="true" className="absolute inset-0 z-0 bg-fixed hidden lg:block" style={layer(DESKTOP)} />
      <div
        aria-hidden="true"
        className="absolute inset-0 z-[1] bg-[color-mix(in_srgb,var(--vibe-color-secondary)_88%,transparent)]"
      />
    </>
  );
}

/**
 * Standalone decorative band using the fixed wave texture. `className` styles
 * the wrapper (height, margins, z-index); it must give the band a height.
 */
export function FixedWaveBand({ className = '' }: { className?: string }) {
  return (
    <div aria-hidden="true" className={`relative overflow-hidden ${className}`}>
      <FixedWaveTexture />
    </div>
  );
}
